import React, { useEffect, useState } from "react";
import { FullscreenOutlined } from "@ant-design/icons";
import { Button, Card, message, Modal, Statistic, Switch, Tooltip } from "antd";
import { Footer, Sidebar } from "../../../../components";
import { getGraphData } from "../../../../utils/api";
import VisualisationDropdowns from "./VisualisationDropdowns";
import { DetailedVisualisationTif } from "./DetailedVisualisationTif";

const METRIC_LABELS = {
  total_broccoli_heads: "Total Detections",
  average_length_cm: "Average Length (cm)",
  std_dev_cm: "Std Dev (cm)",
};

const formatMetricLabel = (key) => {
  if (METRIC_LABELS[key]) {
    return METRIC_LABELS[key];
  }

  return key
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatMetricValue = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    if (Number.isInteger(value)) {
      return value.toLocaleString();
    }

    return value.toLocaleString(undefined, {
      maximumFractionDigits: 3,
    });
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return value;
};

const BroccoliVisualisationDashboard = ({
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  siteData,
  setSiteData,
  currentSite,
  setCurrentSite,
  siteLoading,
  setSiteLoading,
  currentCrop,
  setCurrentCrop,
  cropLoading,
  setCropLoading,
  cropData,
  setCropData,
  searchParams,
  setSearchParams,
  settingDefaults,
  setSettingDefaults,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [siteMetadata, setSiteMetadata] = useState([]);
  const [currentGreenhouse, setCurrentGreenhouse] = useState(null);
  const [currentPlot, setCurrentPlot] = useState(null);
  const [plotData, setPlotData] = useState([]);
  const [greenhouseData, setGreenhouseData] = useState([]);
  const [currentDate, setCurrentDate] = useState(null);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [graphLoading, setGraphLoading] = useState(false);
  const [dateData, setDateData] = useState([]);
  const [selectedPlotHarvestData, setSelectedPlotHarvestData] = useState(null);
  const [isTifFullscreenOpen, setIsTifFullscreenOpen] = useState(false);
  const [showMetricCards, setShowMetricCards] = useState(false);
  const fullscreenInset = "clamp(8px, 2.2vw, 24px)";
  const fullscreenWrapperPadding = "clamp(4px, 1.1vw, 12px)";

  useEffect(() => {
    let ignore = false;

    const fetchSelectedPlotData = async () => {
      if (
        !currentOrg ||
        !currentSite?.id ||
        !currentCrop?.id ||
        !currentGreenhouse?.id ||
        !currentPlot?.id ||
        !currentDate ||
        dropdownLoading
      ) {
        setSelectedPlotHarvestData(null);
        setGraphLoading(false);
        return;
      }

      setGraphLoading(true);
      setSelectedPlotHarvestData(null);

      try {
        const graphRes = await getGraphData(
          currentOrg,
          currentSite.id,
          currentCrop.id,
          currentPlot.id,
          currentDate,
          currentGreenhouse.id,
        );

        if (ignore) {
          return;
        }

        const graphData = graphRes?.data?.data || {};
        const plotSeries =
          graphData?.[currentPlot.name] || Object.values(graphData)?.[0] || [];

        const selectedDateEntry = Array.isArray(plotSeries)
          ? plotSeries.find((entry) => entry?.harvest_date === currentDate)
          : null;

        const harvestData = selectedDateEntry?.data || null;

        setSelectedPlotHarvestData(harvestData);
      } catch (err) {
        if (ignore) {
          return;
        }

        setSelectedPlotHarvestData(null);

        const msg =
          err?.response?.data?.detail || "Oops! Error fetching plot data!";
        messageApi.open({
          type: "error",
          content: msg,
        });
      } finally {
        if (!ignore) {
          setGraphLoading(false);
        }
      }
    };

    fetchSelectedPlotData();

    return () => {
      ignore = true;
    };
  }, [
    currentOrg,
    currentSite,
    currentCrop,
    currentGreenhouse,
    currentPlot,
    currentDate,
    dropdownLoading,
    messageApi,
  ]);

  const metricCards = Object.entries(selectedPlotHarvestData || {})
    .filter(([, value]) => value !== null && !Array.isArray(value))
    .filter(([, value]) => typeof value !== "object")
    .sort(([firstKey], [secondKey]) => {
      if (firstKey === "total_broccoli_heads") return -1;
      if (secondKey === "total_broccoli_heads") return 1;
      return firstKey.localeCompare(secondKey);
    });

  return (
    <>
      <div id="dashboard">
        <Sidebar
          active="visualisation"
          open={sidebarOpen}
          setOpen={setSidebarOpen}
        />
        <div
          className={`flex w-full flex-col pb-14 pt-8 lg:pt-6 sm:pt-4 ${
            sidebarOpen ? "pl-[210px] lg:pl-[50px]" : "pl-[50px]"
          } transition-[padding] duration-200 ease-in-out`}
        >
          <div className="mx-auto w-full max-w-[1900px] px-8 xl:px-6 lg:px-3 md:px-2 sm:px-1.5">
            {contextHolder}
            <VisualisationDropdowns
              siteMetadata={siteMetadata}
              setSiteMetadata={setSiteMetadata}
              currentOrg={currentOrg}
              setCurrentOrg={setCurrentOrg}
              orgData={orgData}
              setOrgData={setOrgData}
              orgLoading={orgLoading}
              setOrgLoading={setOrgLoading}
              currentSite={currentSite}
              setCurrentSite={setCurrentSite}
              siteData={siteData}
              setSiteData={setSiteData}
              siteLoading={siteLoading}
              setSiteLoading={setSiteLoading}
              currentCrop={currentCrop}
              setCurrentCrop={setCurrentCrop}
              cropData={cropData}
              setCropData={setCropData}
              cropLoading={cropLoading}
              setCropLoading={setCropLoading}
              currentPlot={currentPlot}
              setCurrentPlot={setCurrentPlot}
              currentGreenhouse={currentGreenhouse}
              setCurrentGreenhouse={setCurrentGreenhouse}
              greenhouseData={greenhouseData}
              setGreenhouseData={setGreenhouseData}
              plotData={plotData}
              setPlotData={setPlotData}
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
              setDropdownLoading={setDropdownLoading}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              settingDefaults={settingDefaults}
              setSettingDefaults={setSettingDefaults}
              dateData={dateData}
              setDateData={setDateData}
            />
            {Object.keys(siteMetadata).length === 0 && !dropdownLoading ? (
              <div className="text-2xl text-center">
                Metadata not Configured!
              </div>
            ) : (
              <div>
                <div className="mb-4 flex w-full justify-end">
                  <div className="flex items-center gap-3 px-1 py-2 sm:w-full sm:justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      Show Metrics
                    </span>
                    <Switch
                      checked={showMetricCards}
                      onChange={setShowMetricCards}
                    />
                  </div>
                </div>

                {showMetricCards && (
                  <div className="mb-4 flex w-full flex-row gap-4 sm:flex-col">
                    {graphLoading
                      ? Array.from({ length: 4 }).map((_, index) => (
                          <Card
                            key={`metric-skeleton-${index}`}
                            loading={true}
                            variant="outlined"
                            className="w-full flex-1 min-w-0 rounded-md shadow-md"
                          />
                        ))
                      : metricCards.map(([key, value]) => (
                          <Card
                            key={key}
                            variant="outlined"
                            className="w-full flex-1 min-w-0 rounded-md shadow-md transition-shadow duration-300 hover:shadow-2xl"
                          >
                            <Statistic
                              title={formatMetricLabel(key)}
                              value={formatMetricValue(value)}
                            />
                          </Card>
                        ))}
                  </div>
                )}

                <div className="relative flex flex-col">
                  <Tooltip title="Open fullscreen">
                    <Button
                      type="text"
                      aria-label="Open detailed visualisation TIF in fullscreen"
                      icon={<FullscreenOutlined />}
                      onClick={() => setIsTifFullscreenOpen(true)}
                      className="!absolute right-2 top-2 z-20 !text-primaryBlue"
                    />
                  </Tooltip>
                  <DetailedVisualisationTif
                    currentOrg={currentOrg}
                    currentSite={currentSite}
                    siteMetadata={siteMetadata}
                    currentCrop={currentCrop}
                    currentGreenhouse={currentGreenhouse}
                    currentPlot={currentPlot}
                    currentDate={currentDate}
                  />
                </div>
                <Modal
                  open={isTifFullscreenOpen}
                  onCancel={() => setIsTifFullscreenOpen(false)}
                  footer={null}
                  title={null}
                  width={`calc(100% - ${fullscreenInset})`}
                  centered={true}
                  destroyOnHidden={true}
                  styles={{
                    wrapper: {
                      overflow: "hidden",
                      padding: fullscreenWrapperPadding,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    },
                    body: {
                      padding: 0,
                      height: `calc(100dvh - ${fullscreenInset})`,
                      overflow: "hidden",
                    },
                    content: {
                      padding: 0,
                      height: `calc(100dvh - ${fullscreenInset})`,
                      overflow: "hidden",
                    },
                  }}
                  style={{
                    paddingBottom: 0,
                    maxWidth: "none",
                    margin: "0 auto",
                  }}
                  className="!max-w-none"
                >
                  <div className="h-full min-h-0 overflow-hidden p-2 lg:p-1.5 md:p-1 sm:p-0.5">
                    <DetailedVisualisationTif
                      currentOrg={currentOrg}
                      currentSite={currentSite}
                      siteMetadata={siteMetadata}
                      currentCrop={currentCrop}
                      currentGreenhouse={currentGreenhouse}
                      currentPlot={currentPlot}
                      currentDate={currentDate}
                      isFullscreen={true}
                    />
                  </div>
                </Modal>
              </div>
            )}
          </div>
        </div>
        <Footer sidebarOpen={sidebarOpen} />
      </div>
    </>
  );
};

export default BroccoliVisualisationDashboard;
